/**
 * ImportProgressBar - 导入进度条组件
 * 用于显示云端导入任务的进度
 *
 * 按照 3.0-phase-2-import-plan.md Section 5.2 设计：
 * - 位置：二级侧边栏顶部
 * - 显示：整体进度、当前阶段、详细统计
 * - 操作：暂停、取消
 */

import React from 'react';
import { Progress, Button, Tooltip } from 'antd';
import {
  Pause as PauseIcon,
  Play as PlayIcon,
  X as XIcon,
} from 'lucide-react';
import type {
  CloudImportTask,
  CloudImportProgress,
  CloudImportTaskStatus,
} from '../types';

export interface ImportProgressBarProps {
  /** 导入任务 */
  task: CloudImportTask | null;
  /** 进度信息 */
  progress: CloudImportProgress | null;
  /** 暂停回调 */
  onPause?: () => void;
  /** 恢复回调 */
  onResume?: () => void;
  /** 取消回调 */
  onCancel?: () => void;
  /** 关闭回调（任务完成后） */
  onClose?: () => void;
  /** 是否显示 */
  visible?: boolean;
}

/**
 * 获取状态显示文本
 */
const getStatusText = (status: CloudImportTaskStatus): string => {
  switch (status) {
    case 'PENDING':
      return '准备中...';
    case 'RUNNING':
      return '导入中...';
    case 'PAUSED':
      return '已暂停';
    case 'COMPLETED':
      return '导入完成';
    case 'FAILED':
      return '导入失败';
    case 'CANCELLED':
      return '已取消';
    default:
      return '未知状态';
  }
};

/**
 * 获取进度条状态
 */
const getProgressStatus = (status: CloudImportTaskStatus): 'active' | 'success' | 'exception' | 'normal' => {
  switch (status) {
    case 'RUNNING':
      return 'active';
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'exception';
    case 'PAUSED':
    case 'CANCELLED':
    default:
      return 'normal';
  }
};

export const ImportProgressBar: React.FC<ImportProgressBarProps> = ({
  task,
  progress,
  onPause,
  onResume,
  onCancel,
  onClose,
  visible = true,
}) => {
  if (!visible || !task) {
    return null;
  }

  const { status } = task;
  const percentage = progress?.percentage ?? 0;
  const currentPhase = progress?.currentPhase ?? getStatusText(status);

  // 是否可以暂停/恢复
  const canPause = status === 'RUNNING';
  const canResume = status === 'PAUSED';
  const canCancel = status === 'RUNNING' || status === 'PAUSED';
  const isFinished = status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED';

  // 详细进度文本
  const getDetailText = () => {
    const parts: string[] = [];

    if (task.totalPosts > 0) {
      parts.push(`帖子: ${task.processedPosts}/${task.totalPosts}`);
    }
    if (task.totalImages > 0) {
      parts.push(`图片: ${task.uploadedImages}/${task.totalImages}`);
    }
    if (task.totalVideos > 0) {
      parts.push(`视频: ${task.uploadedVideos}/${task.totalVideos}`);
    }

    return parts.join(' | ') || '准备数据中...';
  };

  return (
    <div
      style={{
        padding: '12px 16px',
        backgroundColor: 'var(--ant-color-fill-quaternary)',
        borderBottom: '1px solid var(--ant-color-split)',
        borderRadius: 0,
      }}
    >
      {/* 标题行 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--ant-color-text)' }}>
          {currentPhase}
        </span>
        <span style={{ fontSize: 13, color: 'var(--ant-color-primary)', fontWeight: 600 }}>
          {percentage}%
        </span>
      </div>

      {/* 进度条 */}
      <Progress
        percent={percentage}
        status={getProgressStatus(status)}
        showInfo={false}
        strokeColor="var(--ant-color-primary)"
        trailColor="var(--ant-color-fill-secondary)"
        size="small"
        style={{ marginBottom: 8 }}
      />

      {/* 详细信息和操作按钮 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* 详细进度 */}
        <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
          {getDetailText()}
        </span>

        {/* 操作按钮 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {/* 暂停/恢复按钮 */}
          {canPause && (
            <Tooltip title="暂停">
              <Button
                type="text"
                size="small"
                icon={<PauseIcon size={14} />}
                onClick={onPause}
                style={{ color: 'var(--ant-color-text-secondary)' }}
              />
            </Tooltip>
          )}
          {canResume && (
            <Tooltip title="继续">
              <Button
                type="text"
                size="small"
                icon={<PlayIcon size={14} />}
                onClick={onResume}
                style={{ color: 'var(--ant-color-primary)' }}
              />
            </Tooltip>
          )}

          {/* 取消按钮 */}
          {canCancel && (
            <Tooltip title="取消">
              <Button
                type="text"
                size="small"
                icon={<XIcon size={14} />}
                onClick={onCancel}
                style={{ color: 'var(--ant-color-error)' }}
              />
            </Tooltip>
          )}

          {/* 关闭按钮（任务完成后） */}
          {isFinished && (
            <Tooltip title="关闭">
              <Button
                type="text"
                size="small"
                icon={<XIcon size={14} />}
                onClick={onClose}
                style={{ color: 'var(--ant-color-text-tertiary)' }}
              />
            </Tooltip>
          )}
        </div>
      </div>

      {/* 错误信息（如果有） */}
      {status === 'FAILED' && task.errorMessages && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            backgroundColor: 'var(--ant-color-error-bg)',
            borderRadius: 4,
            fontSize: 12,
            color: 'var(--ant-color-error)',
          }}
        >
          {task.errorMessages.split('\n').slice(0, 3).join('; ')}
          {task.errorMessages.split('\n').length > 3 && '...'}
        </div>
      )}

      {/* 完成统计（如果完成） */}
      {status === 'COMPLETED' && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            backgroundColor: 'var(--ant-color-success-bg)',
            borderRadius: 4,
            fontSize: 12,
            color: 'var(--ant-color-success)',
          }}
        >
          导入成功！
          {task.skippedCount > 0 && ` (跳过 ${task.skippedCount} 项)`}
        </div>
      )}
    </div>
  );
};

export default ImportProgressBar;
